/**
 * Pluggable time-zone providers.
 *
 * All engine time math (wall clock ↔ UTC, gap/overlap handling, zone transitions) goes
 * through the *current* `ZoneProvider` (see `setZoneProvider` in time.ts). The default is
 * `IntlZoneProvider` — the runtime's `Intl.DateTimeFormat` and its tzdata. Platforms whose
 * Intl is old or lacks `timeZone` support (smart-TV browsers) install `TzdbZoneProvider`
 * (`@signetix/schedule-engine/tzdb`, bundled IANA data) instead.
 *
 * Units: instants and offsets in milliseconds; a *wall-clock value* ("naive ms") is the local
 * date/time encoded as if it were UTC (see time.ts).
 */
export const MS_PER_SECOND = 1000;
export const MS_PER_HOUR = 3_600_000;
export const MS_PER_DAY = 86_400_000;
export class UnknownTimeZoneError extends Error {
    zone;
    constructor(zone) {
        super(`Unknown time zone: ${zone}`);
        this.zone = zone;
        this.name = 'UnknownTimeZoneError';
    }
}
/** IANA-style zone names only (offset strings like `+01:00` are rejected by every provider). */
export const IANA_NAME = /^[A-Za-z][A-Za-z0-9_+-]*(\/[A-Za-z0-9_+-]+)*$/;
function utcFields(y, mo, d, h, mi, s) {
    const dt = new Date(0);
    dt.setUTCFullYear(y, mo - 1, d);
    dt.setUTCHours(h, mi, s, 0);
    return dt.getTime();
}
const SCAN_STEP = 6 * MS_PER_HOUR;
/**
 * The runtime's `Intl.DateTimeFormat` (second precision). Assumes, like every current tzdata
 * zone, at most one offset transition within any 24 h window and offsets within ±18 h.
 */
export class IntlZoneProvider {
    id = 'intl';
    formatters = new Map();
    formatter(zone) {
        let f = this.formatters.get(zone);
        if (f === undefined) {
            f = null;
            if (IANA_NAME.test(zone)) {
                try {
                    f = new Intl.DateTimeFormat('en-US', {
                        timeZone: zone,
                        hourCycle: 'h23',
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                        second: 'numeric',
                    });
                }
                catch {
                    f = null;
                }
            }
            this.formatters.set(zone, f);
        }
        return f;
    }
    isKnownZone(zone) {
        return this.formatter(zone) !== null;
    }
    /** Wall-clock value of `instantMs` (kept separate so the sub-second carry is exact). */
    wall(instantMs, zone) {
        const f = this.formatter(zone);
        if (f === null)
            throw new UnknownTimeZoneError(zone);
        const whole = Math.floor(instantMs / MS_PER_SECOND) * MS_PER_SECOND;
        let y = 1970;
        let mo = 1;
        let d = 1;
        let h = 0;
        let mi = 0;
        let s = 0;
        for (const p of f.formatToParts(new Date(whole))) {
            switch (p.type) {
                case 'year':
                    y = Number(p.value);
                    break;
                case 'month':
                    mo = Number(p.value);
                    break;
                case 'day':
                    d = Number(p.value);
                    break;
                case 'hour':
                    h = Number(p.value) % 24;
                    break;
                case 'minute':
                    mi = Number(p.value);
                    break;
                case 'second':
                    s = Number(p.value);
                    break;
                default: break;
            }
        }
        return utcFields(y, mo, d, h, mi, s) + (instantMs - whole);
    }
    offsetAt(instantMs, zone) {
        return this.wall(instantMs, zone) - instantMs;
    }
    validOffsets(naive, zone) {
        const probes = new Set([
            this.offsetAt(naive - MS_PER_DAY, zone),
            this.offsetAt(naive, zone),
            this.offsetAt(naive + MS_PER_DAY, zone),
        ]);
        const out = [];
        for (const o of probes) {
            if (this.wall(naive - o, zone) === naive)
                out.push(o);
        }
        return out.sort((a, b) => b - a);
    }
    bisect(zone, lo, hi, o) {
        while (hi - lo > 1) {
            const mid = lo + Math.floor((hi - lo) / 2);
            if (this.offsetAt(mid, zone) !== o)
                hi = mid;
            else
                lo = mid;
        }
        return hi;
    }
    transitionsBetween(zone, fromMs, toMs) {
        const out = [];
        let t = fromMs;
        let o = this.offsetAt(t, zone);
        while (t < toMs) {
            const next = Math.min(t + SCAN_STEP, toMs);
            const o2 = this.offsetAt(next, zone);
            if (o2 !== o) {
                out.push(this.bisect(zone, t, next, o));
                o = o2;
            }
            t = next;
        }
        return out;
    }
    nextTransition(zone, afterMs, untilMs) {
        let t = afterMs;
        const o = this.offsetAt(t, zone);
        while (t < untilMs) {
            const next = Math.min(t + SCAN_STEP, untilMs);
            if (this.offsetAt(next, zone) !== o)
                return this.bisect(zone, t, next, o);
            t = next;
        }
        return null;
    }
    previousTransition(zone, beforeMs, sinceMs) {
        let t = beforeMs;
        const o = this.offsetAt(t, zone);
        while (t > sinceMs) {
            const prev = Math.max(t - SCAN_STEP, sinceMs);
            const op = this.offsetAt(prev, zone);
            if (op !== o)
                return this.bisect(zone, prev, t, op);
            t = prev;
        }
        return null;
    }
}
//# sourceMappingURL=zone.js.map